import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bot, Check, Copy, KeyRound, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Preset = 'observe' | 'publish' | 'manage';

interface McpToken {
  tokenId: string;
  tokenPrefix: string;
  name: string;
  scopes: string[];
  createdAt: number;
  expiresAt: number | null;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

interface McpListResponse {
  data: {
    endpoint: string;
    tokens: McpToken[];
  };
}

interface McpCreateResponse {
  data: {
    endpoint: string;
    key: string;
    token: McpToken;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function responseError(body: unknown, fallback: string): string {
  if (!isRecord(body)) return fallback;
  if (typeof body.error === 'string') return body.error;
  if (typeof body.message === 'string') return body.message;
  return fallback;
}

async function mcpApi<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const token = localStorage.getItem('token');
  const baseUrl = import.meta.env.VITE_API_URL || '';
  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const parsed: unknown = await response.json().catch(() => ({}));
  if (!response.ok || (isRecord(parsed) && parsed.success === false)) {
    throw new Error(responseError(parsed, response.statusText || 'MCP API request failed'));
  }
  return parsed as T;
}

interface CreatedSecret {
  endpoint: string;
  key: string;
  token: McpToken;
}

const MCP_ORGANIZATION_STORAGE_KEY = 'airgapsuite:mcp:organization';

function restoredOrganizationId(isSuperAdmin: boolean): string {
  if (!isSuperAdmin) return '';
  try {
    return localStorage.getItem(MCP_ORGANIZATION_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

const presetDescriptions: Record<Preset, string> = {
  observe: 'Read across licensing, access, analytics, billing, integrations, logs, and downloads.',
  publish: 'Observe plus non-destructive organization, catalog, policy, user, webhook, partner, and order writes.',
  manage: 'Full hosted catalog, including confirmed destructive and billing operations.',
};

function dateLabel(value: number | null): string {
  if (!value) return 'Never';
  return new Date(value * 1000).toLocaleString();
}

function CopyButton({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={copy}>
      {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

function secretConfigurations(secret: CreatedSecret) {
  const codex = `[mcp_servers.airgapsuite]\nurl = "${secret.endpoint}"\nbearer_token_env_var = "AIRGAPSUITE_MCP_TOKEN"\ndefault_tools_approval_mode = "writes"`;
  const claude = [
    `claude mcp add --transport http airgapsuite ${secret.endpoint} \\`,
    '  --header "Authorization: Bearer ${AIRGAPSUITE_MCP_TOKEN}"',
  ].join('\n');
  const generic = JSON.stringify(
    {
      mcpServers: {
        airgapsuite: {
          type: 'streamable-http',
          url: secret.endpoint,
          headers: { Authorization: 'Bearer ${AIRGAPSUITE_MCP_TOKEN}' },
        },
      },
    },
    null,
    2
  );
  const smoke = [
    `export MCP_URL='${secret.endpoint}'`,
    "export MCP_TOKEN='secret-from-your-secret-manager'",
    'node "$CODEX_HOME/skills/add-cloudflare-mcp/scripts/mcp-smoke.mjs" \\',
    "  --read-tool airgapsuite_list_licenses --read-args '{\"limit\":1}'",
  ].join('\n');
  return { codex, claude, generic, smoke };
}

export function McpAccess({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [endpoint, setEndpoint] = useState('');
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<Preset>('observe');
  const [expiresAt, setExpiresAt] = useState('');
  const [organizationId, setOrganizationId] = useState(() => restoredOrganizationId(isSuperAdmin));
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [created, setCreated] = useState<CreatedSecret | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(false);

  // Super admins pick which org the key is bound to; load the org list.
  useEffect(() => {
    if (!isSuperAdmin) return;
    mcpApi<{ data?: { organizations?: { id: string; name: string }[] } }>('GET', '/api/organizations?limit=1000')
      .then((res) => {
        const organizations = (res.data?.organizations || []).map((o) => ({ id: o.id, name: o.name }));
        setOrgs(organizations);
        setOrganizationId((current) => {
          if (!current || organizations.some((organization) => organization.id === current)) {
            return current;
          }
          localStorage.removeItem(MCP_ORGANIZATION_STORAGE_KEY);
          return '';
        });
      })
      .catch(() => {});
  }, [isSuperAdmin]);

  const selectOrganization = (value: string) => {
    setOrganizationId(value);
    localStorage.setItem(MCP_ORGANIZATION_STORAGE_KEY, value);
  };

  const tenantQuery = isSuperAdmin && organizationId.trim()
    ? `?organizationId=${encodeURIComponent(organizationId.trim())}`
    : '';

  const loadTokens = useCallback(async () => {
    if (isSuperAdmin && !organizationId.trim()) {
      setTokens([]);
      setEndpoint('');
      setLoadingTokens(false);
      return;
    }
    setLoadingTokens(true);
    try {
      const response = await mcpApi<McpListResponse>('GET', `/api/mcp/tokens${tenantQuery}`);
      setTokens(response.data.tokens);
      setEndpoint(response.data.endpoint);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load MCP keys');
    } finally {
      setLoadingTokens(false);
    }
  }, [isSuperAdmin, organizationId, tenantQuery]);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await mcpApi<McpCreateResponse>('POST', '/api/mcp/tokens', {
        name,
        preset,
        expiresAt: expiresAt || undefined,
        organizationId: isSuperAdmin ? organizationId.trim() : undefined,
      });
      setCreated({
        endpoint: response.data.endpoint,
        key: response.data.key,
        token: response.data.token,
      });
      setName('');
      setExpiresAt('');
      await loadTokens();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Failed to create MCP key');
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (token: McpToken) => {
    if (!window.confirm(`Revoke MCP key “${token.name}”? Connected agents will lose access immediately.`)) {
      return;
    }
    try {
      await mcpApi<unknown>('DELETE', `/api/mcp/tokens/${encodeURIComponent(token.tokenId)}${tenantQuery}`);
      await loadTokens();
    } catch (revokeError) {
      setError(revokeError instanceof Error ? revokeError.message : 'Failed to revoke MCP key');
    }
  };

  const configs = useMemo(() => (created ? secretConfigurations(created) : null), [created]);

  return (
    <section className="space-y-6" aria-labelledby="mcp-access-title">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="rounded-md border border-primary/20 bg-primary/10 p-2 text-primary"><Bot className="h-5 w-5" /></div>
            <div>
              <CardTitle id="mcp-access-title">Agent access (MCP)</CardTitle>
              <CardDescription className="mt-1">
                Create organization-bound keys for the hosted Streamable HTTP endpoint at <code>/mcp</code>.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="grid gap-5 md:grid-cols-2 xl:grid-cols-[1.2fr_1fr_1fr_auto] xl:items-end">
            {isSuperAdmin && (
              <div className="space-y-2 md:col-span-2 xl:col-span-4">
                <Label htmlFor="mcp-organization">Organization</Label>
                <Select value={organizationId} onValueChange={selectOrganization}>
                  <SelectTrigger id="mcp-organization">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="mcp-key-name">Key name</Label>
              <Input
                id="mcp-key-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Production reporting agent"
                maxLength={100}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mcp-preset">Access preset</Label>
              <select
                id="mcp-preset"
                value={preset}
                onChange={(event) => setPreset(event.target.value as Preset)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="observe">Observe</option>
                <option value="publish">Publish</option>
                <option value="manage">Manage</option>
              </select>
              <p className="text-xs text-muted-foreground">{presetDescriptions[preset]}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mcp-expiry">Expires (optional)</Label>
              <Input
                id="mcp-expiry"
                type="datetime-local"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={loading || (isSuperAdmin && !organizationId.trim())}>
              <Plus className="mr-2 h-4 w-4" />{loading ? 'Creating…' : 'Create key'}
            </Button>
          </form>
          {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {created && configs && (
        <Card className="border-warning/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-5 w-5 text-amber-500" /> Save this key now
            </CardTitle>
            <CardDescription>The raw key is shown once. Store it in a secret manager, then dismiss it.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-2 rounded-md border bg-background p-3 sm:flex-row sm:items-center">
              <code className="min-w-0 flex-1 break-all text-xs">{created.key}</code>
              <CopyButton label="Copy key" text={created.key} />
            </div>
            <p className="text-xs text-muted-foreground">
              Set it locally with <code>export AIRGAPSUITE_MCP_TOKEN='…'</code>. Do not paste the key into committed config files.
            </p>
            <div className="flex flex-wrap gap-2">
              <CopyButton label="Codex TOML" text={configs.codex} />
              <CopyButton label="Claude Code command" text={configs.claude} />
              <CopyButton label="Generic JSON" text={configs.generic} />
              <CopyButton label="Smoke command" text={configs.smoke} />
              <Button type="button" variant="ghost" size="sm" onClick={() => setCreated(null)}>Dismiss secret</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-5 w-5" /> MCP keys</CardTitle>
            {organizationId && !loadingTokens && (
              <Badge variant="secondary">{tokens.length} configured</Badge>
            )}
          </div>
          <CardDescription>
            {endpoint
              ? `Endpoint: ${endpoint}`
              : isSuperAdmin
                ? 'Choose an organization to view its keys.'
                : 'Organization-scoped agent credentials.'}{' '}
            Revocation is immediate.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Prefix</TableHead><TableHead>Scopes</TableHead>
                <TableHead>Last used</TableHead><TableHead>Expires</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingTokens ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Loading MCP keys…</TableCell></TableRow>
              ) : isSuperAdmin && !organizationId.trim() ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Select an organization to load its MCP keys.</TableCell></TableRow>
              ) : tokens.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">No MCP keys configured for this organization.</TableCell></TableRow>
              ) : tokens.map((token) => {
                const expired = token.expiresAt !== null && token.expiresAt <= Math.floor(Date.now() / 1000);
                const active = token.revokedAt === null && !expired;
                return (
                  <TableRow key={token.tokenId}>
                    <TableCell className="font-medium">{token.name}</TableCell>
                    <TableCell><code className="text-xs">{token.tokenPrefix}…</code></TableCell>
                    <TableCell>
                      <div className="flex max-w-xs flex-wrap gap-1">
                        {token.scopes.slice(0, 4).map((scope) => <Badge key={scope} variant="outline">{scope}</Badge>)}
                        {token.scopes.length > 4 && <Badge variant="secondary">+{token.scopes.length - 4} scopes</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{dateLabel(token.lastUsedAt)}</TableCell>
                    <TableCell>{dateLabel(token.expiresAt)}</TableCell>
                    <TableCell><Badge variant={active ? 'default' : 'secondary'}>{active ? 'Active' : token.revokedAt ? 'Revoked' : 'Expired'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button type="button" variant="ghost" size="sm" disabled={!active} onClick={() => void revoke(token)}>
                        <Trash2 className="mr-2 h-4 w-4" /> Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}
